import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  LayoutGrid, 
  Search,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  description: '',
  active: true,
  sub_rack_count: 1,
  sub_rack_slots: ['']
};

export const Racks = () => {
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchRacks();
  }, []);

  const fetchRacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/racks`, getAuthHeaders());
      setRacks(response.data);
    } catch (err) {
      console.error('Error fetching racks:', err);
      setError('Failed to fetch racks');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingRack(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (rack) => {
    setFormData({
      name: rack.name || '',
      description: rack.description || '',
      active: rack.active ?? true,
      sub_rack_count: rack.sub_racks?.length || rack.sub_rack_count || 1,
      sub_rack_slots: rack.sub_racks?.length
        ? rack.sub_racks.map((subRack) => subRack.slot_count || '')
        : Array.from({ length: rack.sub_rack_count || 1 }, () => '')
    });
    setEditingRack(rack);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Rack name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        sub_rack_count: Number(formData.sub_rack_count) || 1,
        sub_rack_slots: formData.sub_rack_slots.map((value) => (
          value === '' || value === null || value === undefined ? 0 : Math.max(0, Math.floor(Number(value) || 0))
        )),
      };
      if (editingRack) {
        await axios.put(
          `${BACKEND_URL}/api/racks/${editingRack.id}`,
          payload,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/racks`,
          payload,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchRacks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save rack');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rackId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/racks/${rackId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchRacks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete rack');
    }
  };

  const filteredRacks = racks.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="racks-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Racks</h1>
          <p className="text-slate-600 mt-1">Manage storage racks</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-rack-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Rack
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search racks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Racks Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredRacks.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <LayoutGrid className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No racks yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first rack</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Rack
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-5 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Sub Racks</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Description</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredRacks.map((rack) => (
              <div
                key={rack.id}
                data-testid={`rack-row-${rack.id}`}
                className="grid grid-cols-1 md:grid-cols-5 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="font-medium text-slate-900">{rack.name}</div>
                <div>
                  {rack.sub_racks && rack.sub_racks.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {rack.sub_racks.map((sr) => (
                        <span key={sr.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          {sr.name} · {sr.slot_count || 0} slots
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">No sub-racks</span>
                  )}
                </div>
                <div className="text-slate-600 text-sm truncate">{rack.description || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    rack.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {rack.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {rack.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(rack)}
                    data-testid={`edit-btn-${rack.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(rack.id)}
                    data-testid={`delete-btn-${rack.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this rack? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingRack ? 'Edit Rack' : 'Add New Rack'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Number of Sub Racks */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Sub Racks
                  </label>
                  <input
                    type="number"
                    name="sub_rack_count"
                    value={formData.sub_rack_count}
                    onChange={e => {
                      const count = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
                      setFormData((previous) => ({
                        ...previous,
                        sub_rack_count: count,
                        sub_rack_slots: Array.from(
                          { length: count },
                          (_, index) => previous.sub_rack_slots[index] ?? '',
                        ),
                      }));
                    }}
                    disabled={Boolean(editingRack)}
                    min="1"
                    max="100"
                    data-testid="input-sub-rack-count"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {editingRack && <p className="text-xs text-slate-500 mt-1">The number of existing sub-racks cannot be changed here.</p>}
                  {!editingRack && formData.name && formData.sub_rack_count > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Will create: {Array.from({ length: Math.min(formData.sub_rack_count, 10) }, (_, i) => `${formData.name}-${i + 1}`).join(', ')}
                      {formData.sub_rack_count > 10 && `, ... (${formData.sub_rack_count} total)`}
                    </p>
                  )}
                </div>

                {/* Slots per Sub Rack */}
                <div>
                  <div className="mb-2">
                    <h3 className="text-sm font-medium text-slate-700">Slots in Each Sub Rack</h3>
                    <p className="text-xs text-slate-500">Leave a field empty to save zero slots.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1">
                    {Array.from({ length: Number(formData.sub_rack_count) || 0 }, (_, index) => (
                      <div key={index}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          {formData.name ? `${formData.name}-${index + 1}` : `Sub Rack ${index + 1}`}
                        </label>
                        <input type="number" min="0" step="1"
                          value={formData.sub_rack_slots[index] ?? ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFormData((previous) => ({
                              ...previous,
                              sub_rack_slots: previous.sub_rack_slots.map((current, slotIndex) => (
                                slotIndex === index ? value : current
                              )),
                            }));
                          }}
                          data-testid={`input-sub-rack-slots-${index}`}
                          placeholder="0"
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingRack ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
